import React, { useEffect, useState, useRef} from "react";
import Styles from "./Element.module.css";
import TextInput from "../../../../commons/Inputs/TextInput/TextInput";
import { useDispatch, useSelector } from "react-redux";
import SelectInput from "../../../../commons/Inputs/Select/SelectInput";
import { publicCityById } from "../../../../Redux/Slices/publicSlice";
import { findStateInList, findCityInList, getPincodeDetails } from "../../../../helpers/locationHelper";
import { 
  sanitizePincode, 
  sanitizeMobile, 
  sanitizeContactName, 
  checkMobileStart,
  MOBILE_REGEX,
  CONTACT_NAME_REGEX,
  PINCODE_REGEX,
  GENERAL_ERRORS,
  sanitizeGeneralText
} from "../../../../helpers/validators/generalValidators";

const AddressSection = ({
  prefix,
  addressData,
  handleChange,
  errors,
  publicData,
  autoFilledFields,
  unlockField,
  pincodeLoading,
  pincodeErrors,
}) => {
  const dispatch = useDispatch();
  const [citiesList, setCitiesList] = useState([]);
  const selectedStateId = addressData[`${prefix}_state`]?.id;

  useEffect(() => {
    if (selectedStateId) {
      dispatch(publicCityById(selectedStateId)).then((action) => {
        if (action.payload) {
          setCitiesList(action.payload);
        }
      });
    } else {
      setCitiesList([]);
    }
  }, [selectedStateId, dispatch, prefix]);

  const handleGetCity = (field, value) => {
    handleChange(field, value);
    const cityField = field.replace("_state", "_city");
    handleChange(cityField, {});
  };

  const renderTextInput = (
    field,
    heading,
    example,
    type = "text",
    readOnly = false,
  ) => {
    const isAutoFilled = autoFilledFields?.has(field);
    let maxLength = undefined;
    if (field.endsWith("_address")) maxLength = 250;
    if (field.endsWith("_contact")) maxLength = 50;
    if (field.endsWith("_pincode")) maxLength = 6;
    if (field.endsWith("_mobile") || field.endsWith("_altMobile")) maxLength = 10;

    return (
      <div style={{ position: "relative"}}>
        <TextInput
          heading={`${heading} `}
          value={addressData[field]}
          onChange={(value) => handleChange(field, value)}
          maxLength={maxLength}
          example={example}
          type={type}
          height="45"
          error={errors[field]}
          readOnly={isAutoFilled || readOnly}
        />
        {isAutoFilled && (
          <span
            onClick={() => unlockField(field)}
            style={{
              fontSize: "11px",
              color: "var(--primary-color-text)",
              cursor: "pointer",
              position: "absolute",
              top: "4px",
              right: "8px",
              zIndex: 10,
            }}
          >
            🖊 Edit
          </span>
        )}
        {field.endsWith("_pincode") && pincodeLoading?.[field] && (
          <span style={{ fontSize: "11px", color: "var(--primary-color-text)", marginTop: "4px", display: "block" }}>
            🔍 Looking up pincode...
          </span>
        )}
        {field.endsWith("_pincode") && pincodeErrors?.[field] && (
          <span style={{ fontSize: "11px", color: "var(--warning-text)", marginTop: "4px", display: "block" }}>
            ⚠️ {pincodeErrors[field]}
          </span>
        )}
      </div>
    );
  };

  const renderSelectInput = (field, heading, list, example) => {
    const isAutoFilled = autoFilledFields?.has(field);
    return (
      <div style={{ position: "relative"}}>
        <SelectInput
          onChange={(value) =>
            heading === "State *"
              ? handleGetCity(field, value)
              : handleChange(field, value)
          }
          list={list}
          value={addressData[field]}
          example={example}
          heading={`${heading} `}
          height="45px"
          error={errors[field]}
          disabled={isAutoFilled}
        />
        {isAutoFilled && (
          <span
            onClick={() => unlockField(field)}
            style={{
              fontSize: "11px",
              color: "var(--primary-color-text)",
              cursor: "pointer",
              position: "absolute",
              top: "4px",
              right: "8px",
              zIndex: 10,
            }}
          >
            🖊 Edit
          </span>
        )}
      </div>
    );
  };

  return (
    <>
      <div 
        className={Styles.layer1ElementsContainer}
        style={{ marginBottom: errors[`${prefix}_address`] ? "20px" : "0px" }}
      >
        {renderTextInput(`${prefix}_address`, "Full Address *", `Enter Address`)}
      </div>
      <div 
        className={Styles.layer3ElementsContainer}
        style={{ marginBottom: (errors[`${prefix}_pincode`] || errors[`${prefix}_state`] || errors[`${prefix}_city`]) ? "20px" : "0px" }}
      >
        {renderTextInput(`${prefix}_pincode`, "Pin Code *", "Enter Pincode")}
        {renderSelectInput(
          `${prefix}_state`,
          "State *",
          publicData?.stateUT,
          "Eg. Select State"
        )}
        {renderSelectInput(
          `${prefix}_city`,
          "City *",
          citiesList,
          "Eg. Select City"
        )}
      </div>

      <div 
        className={Styles.layer2ElementsContainer}
        style={{ marginBottom: (errors[`${prefix}_contact`] || errors[`${prefix}_email`]) ? "20px" : "0px" }}
      >
        {renderTextInput(
          `${prefix}_contact`,
          "Name of the Contact Person *",
          "Enter Contact Person Name"
        )}
        {renderTextInput(`${prefix}_email`, "Email *", "Enter Email")}
      </div>
      <div 
        className={Styles.layer2ElementsContainer}
        style={{ marginBottom: (errors[`${prefix}_mobile`] || errors[`${prefix}_altMobile`]) ? "20px" : "0px" }}
      >
        {renderTextInput(`${prefix}_mobile`, "Mobile No. *", "Enter Mobile No.")}
        {renderTextInput(
          `${prefix}_altMobile`,
          "Alternate Mobile No.",
          "Enter Alternate Mobile No."
        )}
      </div>
    </>
  );
};


const OfficeDetails = ({ userData, handleData, handleProceed }) => {
  const publicData = useSelector((state) => state?.publicSlice);
    const dispatch = useDispatch();

  const [pincodeLoading, setPincodeLoading] = useState({});
  const [pincodeErrors, setPincodeErrors] = useState({});
  const abortControllers = useRef({});
  const latestPincodeSearch = useRef({});
  const lastLookupPin = useRef({});

  const [autoFilledFields, setAutoFilledFields] = useState(new Set());
  const autoFilledFieldsRef = useRef(autoFilledFields);
  useEffect(() => {
    autoFilledFieldsRef.current = autoFilledFields;
  }, [autoFilledFields]);

  const unlockField = (fieldName) => {
    setAutoFilledFields((prev) => {
      const next = new Set(prev);
      next.delete(fieldName);
      return next;
    });
  };

  const triggerPincodeLookup = async (type, pincode) => {
    const prefix = type.split("_")[0]; // "registered" or "correspondence"
    const stateField = `${prefix}_state`;
    const cityField = `${prefix}_city`;
    const lookupKey = type;

    if (
      lastLookupPin.current[lookupKey] === pincode ||
      latestPincodeSearch.current[lookupKey] === pincode
    ) {
      return;
    }

    if (abortControllers.current[lookupKey]) {
      abortControllers.current[lookupKey].abort();
    }

    const controller = new AbortController();
    abortControllers.current[lookupKey] = controller;
    latestPincodeSearch.current[lookupKey] = pincode;

    setPincodeLoading((prev) => ({ ...prev, [lookupKey]: true }));
    setPincodeErrors((prev) => {
      const next = { ...prev };
      delete next[lookupKey];
      return next;
    });

    try {
      const data = await getPincodeDetails(pincode, controller.signal);
      const postOffices = data[0].PostOffice;
      const stateName = postOffices[0].State;

      const matchedState = findStateInList(stateName, publicData?.stateUT);

      if (matchedState) {
        const citiesAction = await dispatch(publicCityById(matchedState.id));
        const citiesList = citiesAction.payload;

        if (latestPincodeSearch.current[lookupKey] !== pincode) return;

        const matchedCity = findCityInList(postOffices, citiesList);

        const finalState = matchedState;
        const finalCity = matchedCity;

        handleChange(stateField, finalState);
        handleChange(cityField, finalCity);

        setAutoFilledFields((prev) => {
          const next = new Set(prev);
          next.add(stateField);
          next.add(cityField);
          if (sameCheck === "same" && prefix === "registered") {
            next.add("correspondence_state");
            next.add("correspondence_city");
          }
          return next;
        });

        lastLookupPin.current[lookupKey] = pincode;
      } else {
        setPincodeErrors((prev) => ({ 
          ...prev, 
          [lookupKey]: "Couldn't automatically determine the state. Please select it manually." 
        }));
        delete lastLookupPin.current[lookupKey];
      }
    } catch (err) {
      if (err.name !== "CanceledError") {
        setPincodeErrors((prev) => ({ 
          ...prev, 
          [lookupKey]: err.message || "Failed to look up Pincode. Please select state and city manually." 
        }));
        delete lastLookupPin.current[lookupKey];
      }
    } finally {
      if (latestPincodeSearch.current[lookupKey] === pincode) {
        setPincodeLoading((prev) => {
          const next = { ...prev };
          delete next[lookupKey];
          return next;
        });
        delete latestPincodeSearch.current[lookupKey];
      }
    }
  };

  const [addressData, setAddressData] = useState({
    registered_address: userData?.registered_address || "",
    registered_state: userData?.registered_state || {},
    registered_city: userData?.registered_city || {},
    registered_pincode: userData?.registered_pincode || "",
    registered_tel: userData?.registered_tel || "",
    registered_email: userData?.registered_email || "",
    registered_fax: userData?.registered_fax || "",
    registered_contact: userData?.registered_contact || "",
    registered_contactPhone: userData?.registered_contactPhone || "",
    registered_mobile: userData?.phone || userData?.userDetails?.phone || "",
    registered_altMobile: userData?.phone || userData?.userDetails?.phone || "",
    correspondence_address: userData?.correspondence_address || "",
    correspondence_state: userData?.correspondence_state || {},
    correspondence_city: userData?.correspondence_city || {},
    correspondence_pincode: userData?.correspondence_pincode || "",
    correspondence_tel: userData?.correspondence_tel || "",
    correspondence_email: userData?.correspondence_email || "",
    correspondence_fax: userData?.correspondence_fax || "",
    correspondence_contact: userData?.correspondence_contact || "",
    correspondence_contactPhone: userData?.correspondence_contactPhone || "",
    correspondence_mobile:
      userData?.phone || userData?.userDetails?.phone || "",
    correspondence_altMobile:
      userData?.phone || userData?.userDetails?.phone || "",
  });
  
  const addressDataRef = useRef(addressData);
  useEffect(() => {
    addressDataRef.current = addressData;
  }, [addressData]);

  const [sameCheck, setSameCheck] = useState("same");
  const [errors, setErrors] = useState({});

  const handleSameCheckChange = (value) => {
    setSameCheck(value);
    if (value === "same") {
      setAddressData((prev) => {
        const updated = { ...prev };
        Object.keys(prev).forEach((key) => {
          if (key.startsWith("registered_")) {
            const corrKey = key.replace("registered_", "correspondence_");
            updated[corrKey] = prev[key];
          }
        });
        return updated;
      });
      setErrors((prevErrors) => {
        const updatedErrors = { ...prevErrors };
        Object.keys(updatedErrors).forEach((key) => {
          if (key.startsWith("correspondence_")) {
            delete updatedErrors[key];
          }
        });
        return updatedErrors;
      });
    }
  };

  const handleChange = (field, value) => {
    let formattedValue = value;
    if (field === "registered_pincode" || field === "correspondence_pincode") {
      formattedValue = sanitizePincode(value);
    }
    if (field === "registered_contact" || field === "correspondence_contact") {
      formattedValue = sanitizeContactName(value).slice(0, 50);
    }
    if (field === "registered_address" || field === "correspondence_address") {
      formattedValue = sanitizeGeneralText(value).slice(0, 250);
    }
    let mobileError = "";
    if (
      field === "registered_mobile" ||
      field === "correspondence_mobile" ||
      field === "registered_altMobile" ||
      field === "correspondence_altMobile"
    ) {
      formattedValue = sanitizeMobile(value);
      mobileError = checkMobileStart(formattedValue);
    }
    setAddressData((prev) => {
      const updatedData = { ...prev, [field]: formattedValue };
      if (sameCheck === "same" && field.startsWith("registered_")) {
        const correspondenceField = field.replace(
          "registered_",
          "correspondence_"
        );
        updatedData[correspondenceField] = formattedValue;
      }
      return updatedData;
    });
    setErrors((prevErrors) => {
      const nextErrors = { ...prevErrors };
      if (mobileError) {
        nextErrors[field] = mobileError;
      } else {
        delete nextErrors[field];
      }
      return nextErrors;
    });
    if ((field === "registered_pincode" || field === "correspondence_pincode") && formattedValue.length === 6) {
      triggerPincodeLookup(field, formattedValue);
    }
  };

  const validateFields = () => {
    const requiredFields = [
      `registered_address`,
      `registered_state`,
      `registered_city`,
      `registered_pincode`,
      `registered_email`,
      `registered_contact`,
      `registered_mobile`,
    ];

    // Only validate the correspondence address when it differs from the office address.
    if (sameCheck === "different") {
      requiredFields.push(
        `correspondence_address`,
        `correspondence_state`,
        `correspondence_city`,
        `correspondence_pincode`,
        `correspondence_email`,
        `correspondence_contact`,
        `correspondence_mobile`,
      );
    }

    const newErrors = {};
    requiredFields.forEach((field) => {
      if (
        !addressData[field] ||
        ((field.includes("_state") || field.includes("_city")) &&
          !addressData[field]?.id)
      ) {
        newErrors[field] = `${field
          .replace(/([A-Z])/g, " $1")
          .replace(/^./, (str) => str.toUpperCase())
          .replace(/_/g, " ")} is required`;
      }
    });

    [
      "registered_mobile",
      "correspondence_mobile",
      "registered_altMobile",
      "correspondence_altMobile"
    ].forEach((field) => {
      if (addressData[field] && !MOBILE_REGEX.test(addressData[field])) {
        newErrors[field] = GENERAL_ERRORS.mobile;
      }
    });

    ["registered_contact", "correspondence_contact"].forEach((field) => {
      if (addressData[field] && !CONTACT_NAME_REGEX.test(addressData[field])) {
        newErrors[field] = GENERAL_ERRORS.contactName;
      } else if (addressData[field] && addressData[field].length > 50) {
        newErrors[field] = "Contact person name cannot exceed 50 characters";
      }
    });

    ["registered_pincode", "correspondence_pincode"].forEach((field) => {
      if (addressData[field] && !PINCODE_REGEX.test(addressData[field])) {
        newErrors[field] = GENERAL_ERRORS.pincode;
      }
    });


    if (addressData.registered_address && addressData.registered_address.length > 250) {
      newErrors.registered_address = "Address cannot exceed 250 characters";
    }

    if (addressData.correspondence_address && addressData.correspondence_address.length > 250) {
      newErrors.correspondence_address = "Address cannot exceed 250 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinue = (data) => {
    const isValid = validateFields();
    if (!isValid) return;
    const address = [
      {
        type: "OFFICE",
        ...Object.fromEntries(
          Object.entries(addressData)
            .filter(([key]) => key.startsWith("registered_"))
            .map(([key, value]) => [key.replace("registered_", ""), value])
        ),
      },
      {
        type: "CORRESPONDENCE",
        ...Object.fromEntries(
          Object.entries(addressData)
            .filter(([key]) => key.startsWith("correspondence_"))
            .map(([key, value]) => [key.replace("correspondence_", ""), value])
        ),
      },
    ];

    handleData("address", address);
    handleProceed(data);
  };

  useEffect(() => {
    // If userData.address exists, update addressData from it
    if (Array.isArray(userData?.address) && userData.address.length > 0) {
      setAddressData((prev) => ({
        ...prev,
        // Registered (OFFICE)
        registered_address: userData.address[0]?.address || "",
        registered_state: userData.address[0]?.state || {},
        registered_city: userData.address[0]?.city || {},
        registered_pincode: userData.address[0]?.pincode || "",
        registered_tel: userData.address[0]?.tel || "",
        registered_email: userData.address[0]?.email || "",
        registered_fax: userData.address[0]?.fax || "",
        registered_contact: userData.address[0]?.contact || "",
        registered_contactPhone: userData.address[0]?.contactPhone || "",
        registered_mobile:
          userData.address[0]?.mobile || userData?.userDetails?.phone || "",
        registered_altMobile:
          userData.address[0]?.altMobile || userData?.userDetails?.phone || "",
        // Correspondence
        correspondence_address: userData.address[1]?.address || "",
        correspondence_state: userData.address[1]?.state || {},
        correspondence_city: userData.address[1]?.city || {},
        correspondence_pincode: userData.address[1]?.pincode || "",
        correspondence_tel: userData.address[1]?.tel || "",
        correspondence_email: userData.address[1]?.email || "",
        correspondence_fax: userData.address[1]?.fax || "",
        correspondence_contact: userData.address[1]?.contact || "",
        correspondence_contactPhone: userData.address[1]?.contactPhone || "",
        correspondence_mobile:
          userData.address[1]?.mobile || userData?.userDetails?.phone || "",
        correspondence_altMobile:
          userData.address[1]?.altMobile || userData?.userDetails?.phone || "",
      }));
    }
  }, [userData?.address, userData?.userDetails?.phone]);

  return (
    <div className={Styles.elementMain}>
      <div className={Styles.displayInputSection}>
        <div className={Styles.conterntHeader}>Company Address</div>
        <div className={Styles.detailsInpMainContainer}>
          <div
            className={`${Styles.subConterntHeader} ${Styles.corrOtherHeading}`}
          >
            Registered Address
          </div>
          <div className={Styles.divider}></div>
          <AddressSection
            prefix="registered"
            addressData={addressData}
            handleChange={handleChange}
            errors={errors}
            publicData={publicData}
            autoFilledFields={autoFilledFields}
            unlockField={unlockField}
            pincodeLoading={pincodeLoading}
            pincodeErrors={pincodeErrors}
          />

          <div className={Styles.corrAddressContainer}>
            <div
              className={`${Styles.subConterntHeader} ${Styles.corrOtherHeading}`}
            >
              Correspondence Address
            </div>
            <div className={Styles.corrAddSelection}>
              <div className={Styles.officeSelectionText}>
                <div className={Styles.sameCorrHeading}>
                  Same as that of Office Address
                </div>
                <div className={Styles.sel_button}>
                  <span
                    onClick={() => handleSameCheckChange("same")}
                    className={
                      sameCheck === "same"
                        ? Styles.active_corr
                        : Styles.inAct_corr
                    }
                  >
                    Yes
                  </span>
                  <span
                    onClick={() => handleSameCheckChange("different")}
                    className={
                      sameCheck === "different"
                        ? Styles.active_corr
                        : Styles.inAct_corr
                    }
                  >
                    No
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className={Styles.divider}></div>
          {sameCheck === "different" && (
            <AddressSection
              prefix="correspondence"
              addressData={addressData}
              handleChange={handleChange}
              errors={errors}
              publicData={publicData}
              autoFilledFields={autoFilledFields}
              unlockField={unlockField}
              pincodeLoading={pincodeLoading}
              pincodeErrors={pincodeErrors}
            />
          )}
        </div>
      </div>
      <div className={Styles.buttonSection}>
        <div className={Styles.prevBtn} onClick={() => handleProceed("prev")}>
          Back
        </div>
        <div className={Styles.nextBtn} onClick={() => handleContinue("next")}>
          Next
        </div>
      </div>
    </div>
  );
};

export default OfficeDetails;
